import axios from 'axios';

function createApiClient(params) {
  const secret = params.secret || "";
  const version = params.version || "v2";
  const client = axios.create({
    baseURL: `${params.url}/${version}`,
    headers: { common: { Authorization: secret } }
  });
  const updateApiVersion = (newVersion) => {
    client.defaults.baseURL = `${params.url}/${newVersion}`;
  };
  client.interceptors.request.use((config) => {
    for (const key in config.params) {
      if (typeof config.params[key] === "object") {
        config.params[key] = JSON.stringify(config.params[key]);
      } else if (Array.isArray(config.params[key])) {
        config.params[key] = JSON.stringify(config.params[key]);
      }
    }
    return config;
  });
  return {
    client,
    updateApiVersion,
    actions: {
      fetch: async (params2) => {
        return await client.get("/actions", { params: params2 });
      },
      request: async (payload) => {
        return await client.post("/actions", payload);
      },
      retry: async (actionId) => {
        return await client.post(`/actions/${actionId}/retry`);
      }
    },
    admin: {
      impersonate: async (params2) => {
        const headers = { Authorization: `Bearer ${params2.adminOpaqueToken}` };
        return await client.get("/admin/impersonate", {
          headers,
          params: { email: params2.email }
        });
      }
    },
    auth: {
      loginWithEmail: async (payload) => {
        return await client.post("/auth/login/email", payload);
      },
      loginWithGoogle: async (payload) => {
        return await client.post("/auth/login/google", payload);
      },
      logout: async () => {
        return await client.post("/auth/logout");
      },
      password: {
        forgot: async (payload) => {
          return await client.post("/auth/password/forgot", payload);
        },
        reset: async (payload) => {
          return await client.post("/auth/password/reset", payload);
        }
      },
      verificationEmail: {
        resend: async (payload) => {
          return await client.post("/auth/verification/resend", payload);
        },
        verify: async (payload) => {
          return await client.post("/auth/verification/verify", payload);
        }
      }
    },
    billing: {
      oneTimePayment: {
        create: async (payload) => {
          return await client.post("/billing/one-time-payment", payload);
        }
      },
      paymentMethods: {
        fetch: async () => {
          return await client.get("/billing/payment-methods");
        }
      },
      portal: {
        fetch: async () => {
          return await client.get("/billing/portal");
        }
      },
      subscriptions: {
        create: async (payload) => {
          return await client.post("/billing/subscriptions", payload);
        },
        fetch: async () => {
          return await client.get("/billing/subscriptions");
        },
        renew: async () => {
          return await client.post("/billing/subscriptions/renew");
        }
      },
      webhook: {
        create: async (payload) => {
          return await client.post("/billing/webhook", payload);
        }
      }
    },
    events: {
      create: async (payload) => {
        return await client.post("/events", payload);
      }
    },
    companies: {
      ask: async (params2) => {
        return await client.post(
          `/companies/${params2.domain}/ask`,
          {
            explain: params2.explain,
            fields: params2.fields,
            model: params2.model,
            question: params2.question
          }
        );
      },
      count: async (params2) => {
        return await client.get("/companies/count", { params: params2 });
      },
      fetch: async (params2) => {
        return await client.get("/companies", { params: params2 });
      },
      fetchExisting: async (domains) => {
        return await client.post("/companies/existing", { domains });
      },
      fetchSimilar: async (params2) => {
        return await client.get("/companies/similar", { params: params2 });
      },
      fetchOne: async (domain) => {
        return await client.get(`/companies/${domain}`);
      },
      fetchByEmail: async (params2) => {
        return await client.get("/companies/by-email", { params: params2 });
      },
      fetchByName: async (params2) => {
        return await client.get("/companies/by-name", { params: params2 });
      },
      fetchByPrompt: async (params2) => {
        return await client.get("/companies/by-prompt", { params: params2 });
      },
      fetchBySocial: async (params2) => {
        return await client.get("/companies/by-social", { params: params2 });
      },
      fetchContext: async (domain) => {
        return await client.get(`/companies/${domain}/context`);
      },
      analytics: {
        fetch: async (params2) => {
          return await client.get("/companies/analytics", { params: params2 });
        },
        export: async (params2) => {
          return await client.get("/companies/analytics/export", { params: params2 });
        }
      },
      emailPatterns: {
        fetch: async (domain) => {
          return await client.get(`/companies/${domain}/email-patterns`);
        }
      }
    },
    emailPatterns: {
      fetch: async (params2) => {
        return await client.get("/email-patterns", {
          params: {
            emailsCount: params2.emailsCount || false,
            excludeFirstNamePattern: params2.excludeFirstNamePattern || false,
            precision: params2.precision || 0
          }
        });
      },
      fetchOne: async (domain, params2) => {
        return await client.get(`/email-patterns/${domain}`, {
          params: {
            emailsCount: params2.emailsCount || false,
            precision: params2.precision || 0
          }
        });
      }
    },
    industries: {
      fetch: async (params2) => {
        return await client.get("/industries", { params: params2 });
      },
      fetchSimilar: async (params2) => {
        return await client.get("/industries/similar", { params: params2 });
      }
    },
    integrations: {
      fetch: async (params2) => {
        return await client.get("/integrations", { params: params2 });
      },
      settings: {
        create: async (integrationId, payload) => {
          return await client.post(`/integrations/${integrationId}/settings`, payload);
        },
        delete: async (integrationId, integrationSettingsId) => {
          return await client.delete(`/integrations/${integrationId}/settings/${integrationSettingsId}`);
        },
        update: async (integrationId, integrationSettingsId, payload) => {
          return await client.patch(`/integrations/${integrationId}/settings/${integrationSettingsId}`, payload);
        }
      }
    },
    jobTitles: {
      enrich: async (params2) => {
        return await client.get("/job-titles/enrich", { params: params2 });
      }
    },
    lists: {
      create: async (payload) => {
        return await client.post("/lists", payload);
      },
      delete: async (listId) => {
        return await client.delete(`/lists/${listId}`);
      },
      export: async (listId, params2) => {
        return await client.get(`/lists/${listId}/export`, { params: params2 });
      },
      fetch: async (params2) => {
        return await client.get("/lists", { params: params2 });
      },
      sendTestEmail: async (listId) => {
        return await client.post(`/lists/${listId}/test-email`);
      },
      update: async (listId, payload) => {
        return await client.patch(`/lists/${listId}`, payload);
      },
      companies: {
        delete: async (listId) => {
          return await client.delete(`/lists/${listId}/companies`);
        },
        fetch: async (listId, params2) => {
          return await client.get(`/lists/${listId}/companies`, { params: params2 });
        },
        fetchOne: async (listId, domain) => {
          return await client.get(`/lists/${listId}/companies/${domain}`);
        },
        toggle: async (listId, payload) => {
          return await client.patch(`/lists/${listId}/companies/toggle`, payload);
        },
        toggleWithImport: async (listId, payload) => {
          return await client.patch(`/lists/${listId}/companies/toggle/import`, payload);
        },
        toggleWithQuery: async (listId, payload) => {
          return await client.patch(`/lists/${listId}/companies/toggle/query`, payload);
        },
        toggleWithSimilar: async (listId, payload) => {
          return await client.patch(`/lists/${listId}/companies/toggle/similar`, payload);
        }
      },
      integrations: {
        toggle: async (integrationSettingsId, listId, payload) => {
          return await client.patch(`/lists/${listId}/integrations/${integrationSettingsId}/toggle`, payload);
        }
      }
    },
    locations: {
      fetch: async (params2) => {
        return await client.get("/locations", { params: params2 });
      },
      cities: {
        fetch: async (params2) => {
          return await client.get("/locations/cities", { params: params2 });
        }
      },
      continents: {
        fetch: async (params2) => {
          return await client.get("/locations/continents", { params: params2 });
        }
      },
      counties: {
        fetch: async (params2) => {
          return await client.get("/locations/counties", { params: params2 });
        }
      },
      countries: {
        fetch: async (params2) => {
          return await client.get("/locations/countries", { params: params2 });
        }
      },
      query: async (params2) => {
        return await client.get("/locations/query", { params: params2 });
      },
      states: {
        fetch: async (params2) => {
          return await client.get("/locations/states", { params: params2 });
        }
      }
    },
    platform: {
      coverage: {
        fetch: async (payload) => {
          return await client.get("/platform/coverage", { params: payload });
        }
      },
      metrics: {
        fetch: async () => {
          return await client.get("/platform/metrics");
        }
      },
      powerMetrics: {
        fetch: async () => {
          return await client.get("/platform/metrics/power");
        }
      },
      visitors: {
        fetchSession: async (visitorId) => {
          return await client.get("/platform/visitors", { params: { visitorId } });
        }
      }
    },
    power: {
      syncPower: async (payload, options) => {
        return await client.post("/power", payload, options);
      },
      syncData: async (payload, options) => {
        return await client.post("/power/sync", payload, options);
      },
      actions: {
        fetch: async (params2) => {
          return await client.get("/power/actions", { params: params2 });
        },
        fetchOne: async (actionId) => {
          return await client.get(`/power/actions/${actionId}`);
        }
      },
      companies: {
        fetch: async (params2) => {
          return await client.get("/power/companies", { params: params2 });
        },
        fetchBatch: async (params2) => {
          return await client.get("/power/companies/batch", { params: params2 });
        }
      },
      lists: {
        fetch: async (params2) => {
          return await client.get("/power/lists", { params: params2 });
        },
        fetchOne: async (listId) => {
          return await client.get(`/power/lists/${listId}`);
        },
        companies: {
          fetch: async (params2) => {
            return await client.get(`/power/lists/${params2.listId}/companies`, { params: params2 });
          }
        }
      },
      teams: {
        fetchOne: async (teamId) => {
          return await client.get(`/power/teams/${teamId}`);
        },
        fetch: async (params2) => {
          return await client.get("/power/teams", { params: params2 });
        }
      },
      users: {
        fetch: async (params2) => {
          return await client.get("/power/users", { params: params2 });
        },
        fetchOne: async (userId) => {
          return await client.get(`/power/users/${userId}`);
        }
      },
      adminActions: {
        trigger: async (payload) => {
          return await client.post("/power/admin-actions", payload);
        }
      }
    },
    prompts: {
      convertToSegmentation: async (payload) => {
        return await client.post("/prompts/segmentation", payload);
      },
      fetch: async (payload) => {
        return await client.get("/prompts", { params: payload });
      },
      delete: async (promptId) => {
        return await client.delete(`/prompts/${promptId}`);
      },
      processForProduct: async (payload) => {
        return await client.post("/prompts/product", payload);
      },
      processForGetStarted: async (payload) => {
        return await client.post("/prompts/get-started", payload);
      }
    },
    suggestions: {
      fetch: async (params2) => {
        return await client.get("/suggestions", { params: params2 });
      },
      update: async (suggestionCode, payload) => {
        return await client.patch(`/suggestions/${suggestionCode}`, payload);
      }
    },
    teams: {
      create: async (payload) => {
        return await client.post("/teams", payload);
      },
      fetch: async () => {
        return await client.get("/teams");
      },
      fetchOne: async (teamId) => {
        return await client.get(`/teams/${teamId}`);
      },
      switch: async (teamId) => {
        return await client.patch(`/teams/${teamId}/switch`);
      },
      update: async (teamId, payload) => {
        return await client.patch(`/teams/${teamId}`, payload);
      },
      apiTokens: {
        create: async (teamId, payload) => {
          return await client.post(`/teams/${teamId}/api-tokens`, payload);
        },
        delete: async (teamId, apiTokenId) => {
          return await client.delete(`/teams/${teamId}/api-tokens/${apiTokenId}`);
        },
        fetch: async (teamId, payload) => {
          return await client.get(`/teams/${teamId}/api-tokens`, { params: payload });
        },
        update: async (teamId, apiTokenId, payload) => {
          return await client.patch(`/teams/${teamId}/api-tokens/${apiTokenId}`, payload);
        }
      },
      invitations: {
        create: async (teamId, payload) => {
          return await client.post(`/teams/${teamId}/invitations`, payload);
        },
        delete: async (teamId, teamInvitationId) => {
          return await client.delete(`/teams/${teamId}/invitations/${teamInvitationId}`);
        },
        fetch: async (teamId) => {
          return await client.get(`/teams/${teamId}/invitations`);
        }
      },
      notifications: {
        fetch: async (teamId) => {
          return await client.get(`/teams/${teamId}/notifications`);
        }
      },
      settings: {
        fetch: async (teamId) => {
          return await client.get(`/teams/${teamId}/settings`);
        }
      },
      usage: {
        fetch: async (params2) => {
          return await client.get(`/teams/${params2.teamId}/usage`, { params: params2 });
        }
      },
      users: {
        delete: async (teamId, userId) => {
          return await client.delete(`/teams/${teamId}/users/${userId}`);
        },
        fetch: async (teamId) => {
          return await client.get(`/teams/${teamId}/users`);
        }
      },
      webhooks: {
        create: async (teamId, payload) => {
          return await client.post(`/teams/${teamId}/webhooks`, payload);
        },
        delete: async (teamId, webhookId) => {
          return await client.delete(`/teams/${teamId}/webhooks/${webhookId}`);
        },
        fetch: async (teamId) => {
          return await client.get(`/teams/${teamId}/webhooks`);
        },
        sentTestEvent: async (teamId, webhookId) => {
          return await client.post(`/teams/${teamId}/webhooks/${webhookId}/test-event`);
        },
        update: async (teamId, webhookId, payload) => {
          return await client.patch(`/teams/${teamId}/webhooks/${webhookId}`, payload);
        }
      }
    },
    technologies: {
      fetch: async (params2) => {
        return await client.get("/technologies", { params: params2 });
      }
    },
    user: {
      fetch: async () => {
        return await client.get("/user");
      },
      update: async (payload) => {
        return await client.patch("/user", payload);
      },
      updatePassword: async (payload) => {
        return await client.patch("/user/password", payload);
      },
      geolocation: {
        fetch: async () => {
          return await client.get("/user/geolocation");
        }
      },
      invitations: {
        fetch: async () => {
          return await client.get("/user/invitations");
        },
        process: async (teamInvitationToken) => {
          return await client.post(`/user/invitations/${teamInvitationToken}`);
        }
      },
      onboarding: {
        fetch: async () => {
          return await client.get("/user/onboarding");
        },
        update: async (payload) => {
          return await client.patch("/user/onboarding", payload);
        }
      },
      settings: {
        fetch: async () => {
          return await client.get("/user/settings");
        },
        update: async (payload) => {
          return await client.patch("/user/settings", payload);
        }
      }
    },
    utilities: {
      fetchApiHealth: async () => {
        return await axios.get(`${params.url}/`);
      },
      fetchOpenApi: async () => {
        return await client.get("/openapi");
      }
    }
  };
}

export { createApiClient };
